import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { bmpData } from '@/lib/bmpData';
import SEO from '@/components/seo/SEO';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, XCircle, Wrench, ArrowLeft, ClipboardCheck, PenTool } from 'lucide-react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { trackSignupIntent } from '@/lib/tracking';

const BMPDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const bmp = bmpData.find(b => b.slug === slug);
  
  if (!bmp) {
      return (
          <div className="min-h-screen flex flex-col items-center justify-center">
              <h1 className="text-2xl font-bold mb-4">BMP Not Found</h1>
              <Button asChild><Link to="/resources/bmp-field-guide">Return to Guide</Link></Button>
          </div>
      );
  }

  const appSignUpUrl = import.meta.env.VITE_APP_URL 
    ? `${import.meta.env.VITE_APP_URL}/auth?mode=signup`
    : 'http://localhost:3000/auth?mode=signup';

  // OPTIMIZED FOR AI: "HowTo" Schema
  // This explicitly breaks down the inspection process into steps AI can cite.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": `How to Inspect ${bmp.name}`,
    "description": `Step-by-step inspection guide for ${bmp.name} (${bmp.category}) on construction sites. Covers common failures and maintenance.`,
    "step": [
        ...bmp.whatToLookFor.map((step, index) => ({
            "@type": "HowToStep",
            "name": `Check ${index + 1}`,
            "text": step,
            "position": index + 1
        })),
        {
            "@type": "HowToStep",
            "name": "Verify Maintenance",
            "text": "Check that sediment accumulation does not exceed the allowed capacity (usually 1/3 to 1/2 height).",
            "position": bmp.whatToLookFor.length + 1
        }
    ]
  };

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-green-500/20">
      <SEO 
        title={`${bmp.name} Inspection Checklist & Failures`}
        description={`Learn how to properly inspect ${bmp.name}. Common failures like ${bmp.commonFailures[0]?.split(':')[0]} and maintenance tips for SWPPP compliance.`}
        canonical={`/resources/bmp-field-guide/${bmp.slug}`}
        jsonLd={jsonLd}
      />

      <div className="container px-4 py-8 max-w-5xl mx-auto">
        <div className="mb-8">
            <Breadcrumbs items={[
                { label: 'Field Guide', href: '/resources/bmp-field-guide' },
                { label: bmp.name, href: `/resources/bmp-field-guide/${bmp.slug}` }
            ]} />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
                <div>
                    <Badge className="mb-4 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200 border-none">
                        {bmp.category}
                    </Badge>
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-foreground">{bmp.name}</h1>
                    <p className="text-xl text-muted-foreground leading-relaxed">{bmp.description}</p>
                </div>

                {/* ANSWER-FIRST CONTENT */}
                <div className="grid md:grid-cols-2 gap-6">
                    <Card className="border-l-4 border-l-green-500 shadow-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                                <CheckCircle2 /> Inspection Checklist
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ul className="space-y-3">
                                {bmp.whatToLookFor.map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm">
                                        <div className="h-1.5 w-1.5 rounded-full bg-green-500 mt-2 shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-red-500 shadow-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                                <XCircle /> Common Failures
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ul className="space-y-3">
                                {bmp.commonFailures.map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm">
                                        <div className="h-1.5 w-1.5 rounded-full bg-red-500 mt-2 shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>
                </div>

                <div className="bg-muted/30 p-6 rounded-xl border border-border">
                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                        <Wrench size={20} className="text-primary"/> Maintenance & Corrections
                    </h3>
                    <ul className="space-y-2 mb-4">
                        {bmp.maintenanceTips.map((tip, i) => (
                            <li key={i} className="text-muted-foreground text-sm flex items-start gap-2">
                                <span className="font-bold text-foreground">{i + 1}.</span> {tip}
                            </li>
                        ))}
                    </ul>
                    <div className="text-xs font-mono bg-background p-2 rounded border inline-block text-muted-foreground">
                        Inspection Freq: {bmp.frequency}
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                <Card className="bg-primary/5 border-primary/20 sticky top-24">
                    <CardHeader>
                        <CardTitle className="text-lg">Digitize this Checklist</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Stop using paper logs. RunOffTrack has a built-in, customizable checklist for {bmp.name} and 50+ other BMPs.
                        </p>
                        <div className="flex items-center gap-2 text-sm font-medium p-3 bg-background rounded-lg border">
                            <ClipboardCheck size={16} className="text-green-500"/>
                            <span>Add Photo Evidence</span>
                        </div>
                        
                        <div className="flex items-center justify-between gap-2 text-sm font-medium p-3 bg-indigo-50 dark:bg-indigo-900/10 rounded-lg border border-indigo-100 dark:border-indigo-900">
                             <div className="flex items-center gap-2">
                                <PenTool size={16} className="text-indigo-500"/>
                                <span>Draw on Site Map</span>
                             </div>
                             <Link to="/tools/map-sketcher" className="text-xs text-indigo-600 hover:underline">Open Tool</Link>
                        </div>

                        <Button className="w-full shadow-lg" size="lg" asChild>
                            <a href={appSignUpUrl} onClick={() => trackSignupIntent('bmp_detail_cta')}>Start Free Inspection</a>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
      </div>
    </div>
  );
};

export default BMPDetailPage;
